import React, { useEffect, useRef, useState } from 'react';
import { portfolioItems } from '../data/mock';

export const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [visibleItems, setVisibleItems] = useState([]);
  const itemRefs = useRef([]);

  const categories = ['All', 'AI Films', 'Ad Films', 'Shoot-Based Films', 'Social Content', 'Websites'];

  const filteredItems = selectedCategory === 'All' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  useEffect(() => {
    setVisibleItems([]);
    const observers = itemRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems(prev => [...new Set([...prev, index])]);
          }
        },
        { threshold: 0.1 }
      );

      if (ref) {
        observer.observe(ref);
      }

      return observer;
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [filteredItems]);

  return (
    <section id="portfolio" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
            Selected Work
          </h2>
          <p className="text-lg md:text-xl text-gray-600 font-light max-w-3xl mx-auto">
            A glimpse into the stories we've crafted and the brands we've helped evolve.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 text-sm font-light tracking-wide transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-black text-white'
                  : 'bg-white text-black border border-gray-300 hover:border-black'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              ref={el => itemRefs.current[index] = el}
              className={`group cursor-pointer transition-all duration-700 ${
                visibleItems.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Image Container */}
              <div className="relative overflow-hidden aspect-[4/3] mb-4">
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500 flex items-center justify-center">
                  <span className="text-white text-sm tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    VIEW PROJECT
                  </span>
                </div>
              </div>

              {/* Content */}
              <div>
                <div className="text-xs tracking-wider text-gray-500 mb-2 font-light">
                  {item.category}
                </div>
                <h3 className="text-xl font-light mb-2 group-hover:text-gray-600 transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Coming Soon Note */}
        <div className="text-center mt-20">
          <p className="text-gray-500 font-light italic">
            More projects coming soon. Stay tuned.
          </p>
        </div>
      </div>
    </section>
  );
};
